import React, { ChangeEvent, useRef, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { IconButton, TextField } from "@mui/material";
import Typography from "@mui/material/Typography";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { ClearIcon } from "@mui/x-date-pickers";
import { StyleSettings } from "../../styles/StyleSettings";
import SinglePopup from "@widgets/commonpopup/SinglePopup";
import { showPopupModal } from "@widgets/CommonModal";
import { getSearchData } from "../utils/CommonUtils";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { onSearchStore } from "@shared/libs/Global";

interface SnItemDetailProps {
  code?: string;
  defaultValue?: any;
  onValueChanged?: (code: string, name: string, value?: any) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  popupConditions?: any;
}

const SnItemDetail = React.memo(
  ({
    code = "POPUP_ITEM",
    defaultValue,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
    popupConditions = {},
  }: SnItemDetailProps) => {
    const { userInfo } = global();
    const [value, setValue] = useState(defaultValue);
    const [codeValue, setCodeValue] = useState(defaultValue?.code || "");
    const [nameValue, setNameValue] = useState(defaultValue?.name || "");
    const [borderWidth, setBorderWidth] = useState(isRequired ? "2px" : "1px");
    const [backgroundColor, setBackgroundColor] = useState(
      isEditable ? "white" : "#F1F1F1"
    );
    const { runOnSearch } = onSearchStore();
    // tab, enter, blur 이벤트 제어용
    const isOpenRef = useRef(false);
    // const [clearValue, setClearValue] = useState(false);

    // 팝업 세팅용
    const [title, setTitle] = useState("");
    const [autoSearch, setAutoSearch] = useState(true);

    // x버튼 조작용 state
    const [isMouseOver, setMouseOver] = useState(false);
    const [isFocused, setFocused] = useState(false);

    useEffect(() => {
      const singlePopupData = getSearchData(code, undefined);
      setTitle(singlePopupData.title);
      setAutoSearch(singlePopupData.autoSearch);
    }, [defaultValue, code]);

    useEffect(() => {
      setValue(defaultValue || { code: "", name: "" });
      setCodeValue(defaultValue?.code || "");
      setNameValue(defaultValue?.name || "");
    }, [defaultValue]);

    useEffect(() => setBorderWidth(isRequired ? "2px" : "1px"), [isRequired]);

    useEffect(
      () => setBackgroundColor(isEditable ? "white" : "#F1F1F1"),
      [isEditable]
    );

    const openPopup = async (inputValue?: string) => {
      if (isReadOnly) return showDialog("alter", "읽기 전용입니다.");
      if (isOpenRef.current) return;

      isOpenRef.current = true;
      try {
        const mergedData = {
          ...popupConditions,
          str_search: inputValue || codeValue,
        };

        let data: any;
        const result = await showPopupModal({
          title: title,
          children: (
            <SinglePopup
              code={code}
              selectedData={mergedData}
              autoSearch={autoSearch}
            />
          ),
        });
        data = handleReturnFunction(result);
        if (data === "close") {
          setValue(value);
          setCodeValue(codeValue);
          setNameValue(nameValue);
          onValueChanged?.(codeValue, nameValue, value);
          onFocus && onFocus("current");
          return;
        } else if (data.length > 0) {
          setValue(data[0]);
          if (isSearchPanel) {
            setCodeValue(data[0].code);
            setNameValue(data[0].name);
            onValueChanged?.(data[0].code, data[0].name, data[0]);
            await runOnSearch();
            onFocus && onFocus("current");
          } else {
            setCodeValue(data[0].code);
            setNameValue(data[0].name);
            onValueChanged?.(data[0].code, data[0].name, data[0]);
            onFocus && onFocus("next");
          }
        } else {
          // 팝업에서 취소 눌렀을 때
          clearValue();
          await runOnSearch();
        }
      } finally {
        setTimeout(() => {
          isOpenRef.current = false;
        }, 100);
      }
    };

    const handleFocus = () => {
      setFocused(true);
    };

    const handleBlur = async (key?: string) => {
      setFocused(false);

      // 이미 팝업 열려있으면 return
      if (isOpenRef.current) return;

      if (!codeValue) {
        // 입력값이 없을 때
        if ((key === "Enter" || key === "searchBtn") && !isOpenRef.current) {
          await openPopup();
        } else {
          // setClearValue(true);
          clearValue();
          if (key === "Tab") onFocus && onFocus("next");
        }
      } else {
        // 입력값이 있을 때
        if (key === "searchBtn" && !isOpenRef.current) {
          await openPopup(value.code || codeValue);
        } else {
          const data = await checkData();
          if (data && data.length === 1) {
            setValue(data[0]);
            onValueChanged?.(data[0].code, data[0].name, data[0]);
            if (key === "Enter") {
              if (isSearchPanel) {
                // setNameValue(data[0].name);
                setFocused(true);
                await runOnSearch();

                onFocus && onFocus("current");
              } else {
                // setNameValue(data[0].name);
                onFocus && onFocus("next");
              }
            } else if (key === "Tab") {
              onFocus && onFocus("next");
            }
          } else if (!isOpenRef.current) {
            await openPopup(codeValue);
          }
        }
      }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setCodeValue(e.target.value);
    };

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        await handleBlur(e.key);
      }
    };

    const checkData = async () => {
      try {
        const popupSetting = PopupSettings[code];
        if (!popupSetting) return;

        const param1 = {
          id: code.toLowerCase() + "_02",
          params: {
            cd_firm: userInfo.cd_firm,
            ...popupConditions,
            [popupSetting.str_search]: codeValue,
          },
        };
        const result1 = await fnGetDataSet(param1);
        if (result1[0]?.length === 1) return handleReturnFunction(result1[0]);

        const param2 = {
          id: code.toLowerCase() + "_03",
          params: {
            cd_firm: userInfo.cd_firm,
            ...popupConditions,
            str_search: codeValue,
          },
        };
        const result2 = await fnGetDataSet(param2);
        if (result2[0]?.length === 1) return handleReturnFunction(result2[0]);
      } catch (e) {}
    };

    const handleReturnFunction = (data: any) => {
      const popupSetting = PopupSettings[code];
      if (!popupSetting || !data) return [];
      if (data === "close") {
        return data;
      } else {
        return data.map((d: any) => ({
          ...d,
          code: d[popupSetting.columns[0]],
          name: d[popupSetting.columns[1]],
        }));
      }
    };

    const handleClear = async (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      // setClearValue(true);
      setTimeout(() => {
        clearValue();
      }, 0);
      onFocus && onFocus("current");
    };

    const clearValue = () => {
      onValueChanged?.("", "", {});
      setValue({ code: "", name: "" });
      setCodeValue("");
      setNameValue("");
      // if (index && inputEls) inputEls.current[index].value = "";
    };

    return (
      <Card
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          boxShadow: "none",
          p: 0.5, // 안쪽 여백 약간
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          bgcolor: "#fafafa",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <CardContent
            sx={{
              flex: "1 0 auto",
              py: 0, // 🔹 위/아래 여백 최소화
              "&:last-child": { pb: 0 },
            }}
          >
            <TextField
              inputRef={inputRef}
              value={codeValue}
              onFocus={handleFocus}
              onBlur={() => handleBlur()}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onMouseOver={() => setMouseOver(true)}
              onMouseOut={() => setMouseOver(false)}
              fullWidth
              slotProps={{
                input: {
                  autoComplete: "off",
                  endAdornment: (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {(isFocused || isMouseOver) &&
                        !isReadOnly &&
                        (codeValue || nameValue) && (
                          <IconButton
                            edge="end"
                            onClick={handleClear}
                            onMouseDown={(event) => event.preventDefault()}
                            size="small"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      <IconButton
                        onClick={(e) => {
                          openPopup();
                        }}
                        edge="end"
                        tabIndex={-1}
                        size="small"
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ),
                },
              }}
              sx={{
                backgroundColor,
                borderWidth,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth,
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth,
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth,
                  },
                },
                "& .MuiInputBase-root": {
                  height: StyleSettings.control_height,
                  paddingRight: 1,
                },
                "& .MuiInputBase-input": {
                  fontSize: StyleSettings.font,
                },
              }}
            />
          </CardContent>
        </Box>

        <CardContent
          sx={{
            flex: "1 0 auto",
            py: 0,
            ml: 2,
            "&:last-child": { pb: 0 },
          }}
        >
          {/* 품목코드 + 품명: 가로 배치 + 간격 */}
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              품목코드 : {value ? value.cd_item : ""}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              품명 : {value ? value.nm_item : ""}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              Rev : {value ? value.no_rev : ""}
            </Typography>
          </Box>

          {/* 규격: 다음 줄 */}
          <Box sx={{ mt: 0.5 }}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                규격 : {value ? value.dc_item_spec : ""}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                단위 : {value ? value.dc_item_unit : ""}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                모델 : {value ? value.nm_model : ""}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                사용여부 :
                {value
                  ? value.yn_use
                    ? value.yn_use == "Y"
                      ? "예"
                      : "아니오"
                    : ""
                  : ""}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }
);

export default SnItemDetail;
